import React, { useMemo } from 'react'
import Box from '@material-ui/core/Box'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import Typography from '@material-ui/core/Typography'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import List from '@material-ui/core/List'
import Divider from '@material-ui/core/Divider'
import { createStyles, makeStyles } from '@material-ui/core/styles'
import { BLOCKS } from '@contentful/rich-text-types'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import loadable from '@loadable/component'

import { BGVariants } from '../../types/heroTextSection'
import { PackageVariant, SellingPoint } from '../../types/productPageV2'
import { Button, PurchaseButton } from '../../types/page'
import { ExternalLink } from '../../types/page'

import useBrand from '../../hooks/useBrand'
import useHandlebarsContext from '../../hooks/useHandlebarsContext'

import formatContentfulClasses from '../../utils/customClasses'
import insertHandlebarsTemplate from '../../utils/insertHandlebarsTemplate'

import CheckMarkSVG from '../../images/checkmarkblue.inline.svg'
import { createContentfulClassGetter } from '../../utils/contentful'
import { BindToFields, CssEntry } from '../../types/contentfulAttributes'
import { minAffirmAmount } from '../../types/constants'
import { AlternatePlan } from '../../hooks/useSKUs'
import { hasNonNullProperties } from '../../utils/utils'
import { SplitPaymentLink } from './SplitPaymentLink'

const NavigationGroupLink = loadable(
  () => import('../NavigationGroup/NavigationGroupLink')
)
const ProductPriceInsert = loadable(() => import('./ProductPriceInsert'))
const RichText = loadable(() => import('../RichText'))
const LinkButton = loadable(() => import('../Link/LinkButton'))
const Affirm = loadable(() => import('../Affirm'))

export interface PurchaseCardProps {
  title?: string | null
  subtitle?: string | null
  description: string
  sellingPointsHeader?: string | null
  sellingPoints: SellingPoint[]
  cta: PurchaseButton | Button | null
  secondaryCta: ExternalLink
  backgroundColor: string
  discountToken: string
  variant: string
  highlightPackage: boolean
  highlightTag: string
  originalAmount?: number
  amount?: number
  discountedAmount?: number
  formattedAmount?: string
  formattedDiscount?: string | null
  planCode?: string
  purchasePath?: string
  priceOverride?: string
  codeClasses: string[] | null
  css: CssEntry[] | null
  alternatePlan?: AlternatePlan
}

const PurchaseCard: React.FC<PurchaseCardProps> = ({
  title,
  subtitle,
  description,
  sellingPointsHeader,
  sellingPoints,
  amount = 0,
  formattedAmount,
  formattedDiscount,
  planCode,
  cta,
  secondaryCta,
  backgroundColor,
  variant,
  highlightPackage,
  highlightTag,
  purchasePath,
  priceOverride,
  codeClasses,
  css,
  alternatePlan,
}) => {
  const classes = useStyles({})
  const { t } = useTranslation()
  const brand = useBrand()
  const handlebarsContext = useHandlebarsContext()

  const isPricingPagePackage = codeClasses?.includes('pricing-package')

  const applyContentfulStyles = createContentfulClassGetter(css)

  const filteredSellingPoints = useMemo(() => {
    return sellingPoints.filter(({ sellingPointBody }) => {
      try {
        const parsedRawBody = JSON.parse(sellingPointBody.raw)
        const textValue = parsedRawBody?.content?.at(0)?.content?.at(0)?.value
        return textValue !== '-'
      } catch {
        return true
      }
    })
  }, [sellingPoints])

  const getLinkButton = (): JSX.Element | null => {
    if (cta) {
      if (planCode) {
        if (cta.__typename === 'ContentfulButton') {
          return (
            <LinkButton
              className={clsx(...formatContentfulClasses(cta.codeClasses))}
              href={purchasePath}
              color={cta.color === 'default' ? 'primary' : cta.color}
              variant={cta.variant}
              fullWidth
              mixpanelTracking={cta.mixpanelTracking}
            >
              {cta.buttonText ?? undefined}
            </LinkButton>
          )
        }

        if (cta.action) {
          if ('slug' in cta.action) {
            return (
              <LinkButton
                className={clsx(...formatContentfulClasses(cta.codeClasses))}
                to={cta.action.slug ?? purchasePath}
                color={cta.color === 'default' ? 'primary' : cta.color}
                variant={cta.variant}
                fullWidth
                mixpanelTracking={cta.mixpanelTracking}
              >
                {cta.buttonText ?? undefined}
              </LinkButton>
            )
          }

          if ('url' in cta.action) {
            return (
              <LinkButton
                className={clsx(...formatContentfulClasses(cta.codeClasses))}
                href={cta.action.url}
                color={cta.color === 'default' ? 'primary' : cta.color}
                variant={cta.variant}
                fullWidth
                mixpanelTracking={cta.mixpanelTracking}
              >
                {cta.buttonText ?? undefined}
              </LinkButton>
            )
          }
        }
      }

      return (
        <NavigationGroupLink
          key={cta.contentful_id}
          link={cta as Button}
          className={clsx(...formatContentfulClasses(cta.codeClasses))}
        />
      )
    }

    return null
  }
  return (
    <Card
      className={clsx(
        {
          [classes.card]: true,
          [classes.cardDarkPrimary]: backgroundColor === BGVariants.DarkPrimary,
          [classes.cardPopular]: highlightPackage && isPricingPagePackage,
          [classes.threeColPackage]: codeClasses?.includes('three-col-package'),
        },
        formatContentfulClasses(codeClasses),
        applyContentfulStyles(BindToFields.Main)
      )}
      elevation={0}
    >
      {highlightPackage ? (
        <Typography
          className={clsx(
            classes.banner,
            isPricingPagePackage
              ? classes.taggedPopularBanner
              : classes.popularBanner,
            applyContentfulStyles(BindToFields.HighlightTag)
          )}
          component="p"
          variant="overline"
        >
          {highlightTag ? highlightTag : t('purchaseCard.popular')}
        </Typography>
      ) : (
        <Typography
          className={clsx(
            {
              [classes.banner]: true,
            },
            codeClasses?.includes('three-col-package')
              ? classes.hideOnSmall3Col
              : classes.hideOnSmall,
            applyContentfulStyles(BindToFields.HighlightTag)
          )}
          component="p"
          variant="overline"
        >
          &nbsp;
        </Typography>
      )}
      <CardContent
        className={clsx({
          [classes.content]: true,
          [classes.borderRadius]: !highlightPackage && !isPricingPagePackage,
          [classes.contentExpanded]: variant === PackageVariant.expandedPackage,
          [classes.contentMostPopular]:
            highlightPackage && !isPricingPagePackage,
        })}
      >
        <Box
          className={clsx({
            [classes.upperContainer]: true,
            [classes.highlightedContainer]:
              highlightPackage && isPricingPagePackage,
          })}
        >
          <Box
            className={clsx({
              [classes.expandedPackage]:
                variant === PackageVariant.expandedPackage,
            })}
          >
            <Typography
              variant="h3"
              className={clsx(
                {
                  [classes.title]: true,
                  [classes.largeSizedTitle]:
                    codeClasses?.includes('large-sized-title'),
                },
                applyContentfulStyles(BindToFields.TitleProductPackageDetail)
              )}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                className={clsx({
                  [classes.subtitle]: true,
                  [classes.contentfulSubtitle]:
                    codeClasses?.includes('subtitle'),
                })}
                variant="h4"
              >
                {subtitle}
              </Typography>
            )}
            <Typography
              className={clsx(
                classes.description,
                applyContentfulStyles(BindToFields.Description)
              )}
              variant="body1"
            >
              {insertHandlebarsTemplate(description, handlebarsContext)}
            </Typography>
            <Box
              className={clsx({
                [classes.priceContainer]: true,
              })}
            >
              <>
                {formattedAmount ? (
                  <ProductPriceInsert
                    formattedAmount={formattedAmount}
                    formattedDiscount={formattedDiscount}
                    url={purchasePath}
                    linkPriceClass={classes.linkPrice}
                    originalPriceClass={classes.originalPrice}
                  />
                ) : (
                  <>
                    {priceOverride ? (
                      <Typography className={classes.price}>
                        {priceOverride}
                      </Typography>
                    ) : (
                      <Typography className={classes.price}>
                        {t('purchaseCard.free')}
                      </Typography>
                    )}
                  </>
                )}
              </>
              <Box className={classes.affirm}>
                {formattedAmount &&
                planCode &&
                !brand.name?.includes('TopCV') &&
                amount > minAffirmAmount ? (
                  <Affirm planCode={planCode} price={amount} />
                ) : brand.isTopCvFr && hasNonNullProperties(alternatePlan) ? (
                  <SplitPaymentLink
                    planCode={alternatePlan.plan_code}
                    intervalTimes={alternatePlan.interval_times}
                    amount={alternatePlan.amount}
                  />
                ) : (
                  <Typography className={classes.oneTimePayment}>
                    {t('purchaseCard.ineligibleForAffirm')}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          {getLinkButton()}
          <List
            className={clsx({
              [classes.sellingPoints]: true,
              [classes.sellingPointsExpanded]:
                variant === PackageVariant.expandedPackage,
            })}
          >
            {sellingPointsHeader && (
              <span className={classes.sellingPointsHeader}>
                {sellingPointsHeader}
              </span>
            )}
            {filteredSellingPoints.map((point) => (
              <ListItem
                className={clsx({
                  [classes.sellingPoint]: true,
                })}
                disableGutters
                key={point.contentful_id}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <CheckMarkSVG className={classes.icon} />
                </ListItemIcon>
                {point.sellingPointBody && (
                  <RichText
                    rawBody={point.sellingPointBody.raw}
                    references={point.sellingPointBody.references}
                    options={{
                      props: {
                        [BLOCKS.PARAGRAPH]: {
                          variant: 'body2',
                          className: classes.listItem,
                        },
                      },
                    }}
                  />
                )}
              </ListItem>
            ))}
          </List>
        </Box>
        {secondaryCta && (
          <Box>
            <Divider />
            <Box className={classes.lowerContainer}>
              <NavigationGroupLink
                link={secondaryCta}
                className={classes.secondaryCta}
              >
                {secondaryCta.text}
              </NavigationGroupLink>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

const useStyles = makeStyles((theme) =>
  createStyles({
    affirm: {
      minHeight: '2rem',
      color: '#727d92', // @TODO: Add to the TopResumeTheme
    },
    card: {
      backgroundColor: 'transparent',
      borderRadius: theme.shape.borderRadius,
      height: '100%',

      [theme.breakpoints.down('sm')]: {
        padding: 0,
      },
    },
    threeColPackage: {
      '&&': {
        padding: 0,
      },
    },
    cardDarkPrimary: {
      backgroundColor: 'transparent',
      color: theme.palette.background.default,
    },
    cardPopular: {
      position: 'relative',
      overflow: 'visible',
      paddingTop: '4.5rem',
    },
    contentMostPopular: {
      border: `2px solid ${theme.palette.primary.dark}`,
      borderRadius: '0 0 6px 6px',
    },
    borderRadius: {
      borderRadius: theme.shape.borderRadius,
    },
    content: {
      backgroundColor: theme.palette.background.default,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      padding: '0',
    },
    contentExpanded: {
      backgroundColor: theme.palette.background.default,
      minHeight: 'unset',
    },
    description: {
      fontSize: '1.0625rem',
      fontWeight: 700,
      margin: '.516rem 0 2.3rem',
      color: '#7a8498', // @TODO: Add to the TopResumeTheme
      textAlign: 'center',
    },
    expandedHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
      },
    },
    expandedPackage: {
      width: '50%',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    banner: {
      backgroundColor: 'transparent',
      color: theme.palette.background.default,
      lineHeight: 'normal',
      padding: '.86em 0 .79em',
      textAlign: 'center',
    },
    hideOnSmall: {
      [theme.breakpoints.down(768)]: {
        display: 'none',
      },
    },
    hideOnSmall3Col: {
      [theme.breakpoints.down(960)]: {
        display: 'none',
      },
    },
    popularBanner: {
      backgroundColor: theme.palette.primary.dark,
      borderRadius: '6px 6px 0 0',

      '&&': {
        // Increase the specificity to make sure that the display: 'none' doesn't affect
        display: 'block',
      },
    },
    taggedPopularBanner: {
      top: '1.5rem',
      lineHeight: '0.5rem',
      boxSizing: 'inherit',
      backfaceVisibility: 'hidden',
      position: 'absolute',
      left: '-0.5rem',
      padding: '0.7rem 1rem 0.5rem',
      margin: '0',
      letterSpacing: '.1em',
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      fontWeight: 'bold',
      color: '#fff',
      backgroundColor: '#ee8d49',
      zIndex: 3,
      fontFamily: 'proxima-nova,Helvetica,Arial,sans-serif',

      '&:before': {
        content: '""',
        width: '0',
        bottom: '-0.5rem',
        position: 'absolute',
        display: 'block',
        border: '0.5rem solid #e46b15',
        zIndex: -2,
        left: '-1rem',
        borderRightWidth: '0.5rem',
        borderLeftColor: 'transparent',
      },
      '&:after': {
        content: '""',
        bottom: '-0.5rem',
        position: 'absolute',
        display: 'block',
        borderStyle: 'solid',
        borderColor: '#ec7e32 #e46b15 #e46b15 #e46b15',
        zIndex: -1,
        left: '0',
        borderWidth: '0.5rem 0 0 0.5rem',
      },
    },
    purchaseForm: {
      marginTop: 'auto',
      '& > * ': {
        width: '100%',
      },
    },
    sellingPointsHeader: {
      fontSize: '0.94rem',
      fontWeight: 600,
    },
    sellingPoint: {
      padding: '0.38rem 0 .38rem 0',
      alignItems: 'flex-start',
    },
    listItem: {
      '&&': {
        margin: '0.15rem',
        lineHeight: '1.25rem',
      },
    },
    sellingPoints: {
      margin: '1rem 0 0',
      padding: '0',
    },
    sellingPointsExpanded: {
      columnCount: 2,
      [theme.breakpoints.down('sm')]: {
        columnCount: 'unset',
      },
    },
    icon: {
      marginRight: '.1rem',
      width: 'auto',
    },
    listItemIcon: {
      minWidth: '1.45rem',
    },
    cta: {
      margin: '1rem 0',
    },
    secondaryCta: {
      fontWeight: 600,
      fontSize: '0.9375rem',
      lineHeight: '1.25rem',
      '&:hover': {
        color: theme.palette.primary.dark,
      },
    },
    lowerContainer: {
      textAlign: 'center',
      padding: '1.5rem 2rem 0',
    },
    upperContainer: {
      display: 'flex',
      flexDirection: 'column',
      padding: '2rem 2rem 1.5rem',
    },
    highlightedContainer: {
      paddingTop: '3rem',

      [theme.breakpoints.up('md')]: {
        paddingTop: '4.5rem',
      },
    },
    priceContainer: {
      minHeight: '5.28rem',
      marginBottom: '1rem',
      textAlign: 'center',
    },
    price: {
      fontSize: '2.25rem',
      lineHeight: '2.875rem',
      fontWeight: 700,
    },
    originalPrice: {
      '&&': {
        fontSize: '1.625rem',
        lineHeight: '2.875rem',
        fontWeight: 700,
      },
    },
    linkPrice: {
      '&&': {
        fontSize: '2.25rem',
        fontWeight: 700,
        lineHeight: '2.875rem',
      },
    },
    subtitle: {
      fontWeight: 700,
    },
    title: {
      minHeight: '5.25rem',
    },
    largeSizedTitle: {
      fontSize: '2.95em',
      fontWeight: 600,
      textAlign: 'center',
    },
    contentfulSubtitle: {
      textAlign: 'center',
    },
    oneTimePayment: {
      margin: '0',
      fontSize: '.825em',
    },
  })
)

export default PurchaseCard
